import type { ReactNode } from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Brand } from '~/types/user';

import { useDeleteBrandLogo, useUpdateBrandLogo } from '~/api/user';
import { maxImageUploadSize } from '~/constants/constants';
import { toast } from '~/utils/toast';

import { ImageUploadField } from '../../../components/ImageUploadField';

export const BrandLogoInput = ({ brand, description }: { brand: Brand; description?: ReactNode }) => {
  const updateMutation = useUpdateBrandLogo(brand.id);
  const deleteMutation = useDeleteBrandLogo(brand.id);
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);

  const { t } = useTranslation(['settings']);

  return (
    <ImageUploadField
      actionsLayout="row"
      deleteLabel={t('settings:imageUpload.logo.delete')}
      description={description}
      isDeleting={deleteMutation.isPending}
      isUploading={updateMutation.isPending}
      label={t('settings:fields.logo.label')}
      onDelete={() =>
        deleteMutation.mutate(undefined, { onError: () => toast.danger({ description: t('settings:fields.logo.error.delete') }) })
      }
      onUpload={(logo) => {
        if (logo.size > maxImageUploadSize) {
          return toast.danger({ description: t('settings:fields.logo.error.tooLarge') });
        }

        const data = new FormData();
        data.append('logo', logo, logo.name);

        setPendingLogo(logo);
        updateMutation.mutate(data, {
          onError: () => toast.danger({ description: t('settings:fields.logo.error.upload') }),
          onSettled: () => setPendingLogo(null),
        });
      }}
      pendingFile={pendingLogo}
      uploadLabel={t('settings:imageUpload.logo.upload')}
      url={brand.logo}
      variant="logo"
    />
  );
};
